import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Pagination } from '../../../data-ingestion/models/data-ingestion';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { ViewDetailsModalComponent } from '../view-details-modal/view-details-modal.component';
import { AuthService } from '../../../shared/services/auth.service';
import {
  ApprovalStatus,
  Engagements,
  ExecutionStatus,
  QueryRecord,
  Status,
  SyntheticResultStatus,
} from '../../../../API';
import { AppService } from '../../../shared/services/app.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import { AT_LEAST_ONE_ALPHABET } from '../../../shared/utils/constants';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { ROLE } from '../../../shared/models/auth';

dayjs.extend(duration);

@Component({
  selector: 'app-query-data-table',
  templateUrl: './query-data-table.component.html',
  styleUrl: './query-data-table.component.scss',
})
export class QueryDataTableComponent implements OnInit, OnChanges {
  @Input() headers: any[] = [];
  @Input() rows: any[] = [];
  @Output() reloadQueryHistory = new EventEmitter<void>();

  readonly pagination: Pagination = { page: 1, size: 10 };
  readonly supportedPageSizes = [10, 25, 50];
  readonly approveQueryHistoryAction: string[] = ['app.dcr-portal.query-history.action.approve'];
  readonly rejectQueryHistoryAction: string[] = ['app.dcr-portal.query-history.action.reject'];
  readonly viewResultQueryHistoryAction: string[] = ['app.dcr-portal.query-history.result'];
  readonly editQueryBuildTab: string[] = ['app.dcr-portal.query-history.action.edit'];

  totalCount?: number;
  isLoading = false;
  searchValue?: string = '';
  visible = false;
  engagement?: string;
  username?: string;
  userOrgType: string;
  userRole: string;

  rowsToDisplay: any[] = [];

  rejectModalRef!: NzModalRef;
  rejectReason: UntypedFormControl = new FormControl('', [
    Validators.required,
    Validators.minLength(5),
    Validators.pattern(AT_LEAST_ONE_ALPHABET),
  ]);

  constructor(
    private modalService: NzModalService,
    private authService: AuthService,
    private appService: AppService,
    private userManagementService: UserManagementService,
  ) {
    this.rowsToDisplay = [...this.rows];
    this.userOrgType = this.userManagementService.getOrgType();
    this.username = this.userManagementService.getEmail();
    this.userRole = this.userManagementService.getUserRole();
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.rowsToDisplay = this.rows = [...changes['rows'].currentValue];
  }

  ngOnInit(): void {
    this.getPageData();
    /** subscribe for engagement changes */
    this.appService.activeEngagement$.subscribe((engagements: Engagements) => {
      this.engagement = engagements?.engagement;
    });
  }

  reset(): void {
    this.searchValue = '';
    this.search();
  }

  search(): void {
    this.visible = false;
    this.rowsToDisplay = this.rows?.filter(
      (item: any) => item.name?.toLowerCase().indexOf(this.searchValue?.toLowerCase()) !== -1,
    );
  }

  getPageData(): void {
    this.totalCount = this.rowsToDisplay?.length;
  }

  getValidationStatusColor(status: string): string {
    switch (status) {
      case Status.SUCCESS:
        return 'success';
      case Status.IN_PROGRESS:
        return 'warning';
      case Status.FAIL:
        return 'error';
      default:
        return '';
    }
  }

  getApprovalStatusColor(status: string): string {
    switch (status) {
      case ApprovalStatus.APPROVED:
        return 'success';
      case ApprovalStatus.PENDING:
        return 'warning';
      case ApprovalStatus.REJECTED:
        return 'error';
      default:
        return '';
    }
  }

  getExecutionStatusColor(status: string): string {
    switch (status) {
      case ExecutionStatus.SUCCESS:
        return 'success';
      case ExecutionStatus.FAIL:
        return 'error';
      case ExecutionStatus.IN_PROGRESS:
        return 'processing';
      case ExecutionStatus.CANCELLED:
        return 'warning';
      default:
        return '';
    }
  }

  getSyntheticStatusColor(status: string): string {
    switch (status) {
      case SyntheticResultStatus.SUCCESS:
        return 'success';
      case SyntheticResultStatus.FAIL:
        return 'error';
      case SyntheticResultStatus.IN_PROGRESS:
        return 'processing';
      case SyntheticResultStatus.CLEANED:
        return 'warning';
      default:
        return '';
    }
  }

  getExecutionDuration(executionTime: number): string {
    if (executionTime) {
      const durationObj = dayjs.duration(executionTime);
      if (executionTime > 1000 && executionTime < 60000) {
        const seconds = durationObj.asSeconds();
        return `${seconds} sec`;
      } else if (executionTime > 60000) {
        const formattedDuration = durationObj.format('m [min] s [sec]');
        return formattedDuration;
      } else {
        const milliSeconds = durationObj.asMilliseconds();
        return `${milliSeconds} ms`;
      }
    }
    return '--';
  }

  viewQueryRecord(queryData: any) {
    const title =
      this.isUserAnalystOrAdmin() && this.canApproveOrReject(queryData) ? 'Review & Approve' : 'View Query Details';
    this.modalService.create({
      nzTitle: title,
      nzContent: ViewDetailsModalComponent,
      nzData: queryData,
      nzOkText: null,
      nzCancelText: null,
      nzWidth: '70rem',
      nzFooter: null,
    });
  }

  canApproveOrReject(queryData: QueryRecord): boolean {
    if (!this.appService.isEngagementActive) return false;
    const { orgType, approvalStatus, validationStatus } = queryData;

    return (
      this.authService.hasEntitlements(this.approveQueryHistoryAction) &&
      approvalStatus?.toString() === ApprovalStatus.PENDING &&
      validationStatus === Status.SUCCESS &&
      orgType !== this.userOrgType
    );
  }

  canEdit(queryData: QueryRecord): boolean {
    if (!this.appService.isEngagementActive) return false;

    const { orgType, approvalStatus, executionStatus } = queryData;

    return (
      orgType === this.userOrgType &&
      this.authService.hasEntitlements(this.editQueryBuildTab) &&
      (!approvalStatus || ApprovalStatus.REJECTED === approvalStatus || ExecutionStatus.FAIL === executionStatus)
    );
  }

  canViewResult(queryData: QueryRecord): boolean {
    const { executionStatus, approvalStatus } = queryData;

    return (
      this.authService.hasEntitlements(this.viewResultQueryHistoryAction) &&
      ApprovalStatus.APPROVED === approvalStatus &&
      ExecutionStatus.SUCCESS === executionStatus
    );
  }

  isUserAnalystOrAdmin(): boolean {
    return this.userRole === ROLE.ADMIN || this.userRole === ROLE.ANALYST;
  }
}
